/**
 * Text section renderer.
 *
 * Sanitization strategy — DOMPurify (chosen over rehype-sanitize):
 *   - The agency sends content_md that may be raw HTML or Markdown.
 *   - We detect HTML (content starting with '<') and sanitize with DOMPurify
 *     before setting dangerouslySetInnerHTML, stripping all script/event attrs.
 *   - For Markdown input we use react-markdown (remark-gfm), which never
 *     emits raw HTML unless rehype-raw is explicitly enabled (it is not here),
 *     so no sanitization is required for the MD path.
 */
import DOMPurify from 'dompurify';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { TextData } from '../../../types/blog';

interface TextProps {
  data: TextData;
}

const isHtml = (s: string) => /^\s*</.test(s);

const proseClasses =
  'prose max-w-none min-w-0 dark:prose-invert ' +
  'prose-headings:vscode-text-primary prose-headings:font-bold ' +
  'prose-p:leading-relaxed ' +
  'prose-a:underline ' +
  'prose-blockquote:vscode-border ' +
  'prose-code:px-1 prose-code:rounded ' +
  'prose-pre:border vscode-border ' +
  'prose-table:block prose-table:overflow-x-auto';

const mdComponents = {
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto -mx-1 px-1 my-4 max-w-full">
      <table className="min-w-full">{children}</table>
    </div>
  ),
};

const Text: React.FC<TextProps> = ({ data }) => {
  // Accept both `content_md` (editor / normalised DB) and legacy `body` (third-party API payloads).
  const content = data.content_md ?? (data as unknown as { body?: string }).body ?? '';

  if (isHtml(content)) {
    const clean = DOMPurify.sanitize(content, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    });
    return (
      <div className="overflow-x-auto max-w-full">
        <div
          className={proseClasses}
          dangerouslySetInnerHTML={{ __html: clean }}
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-w-full">
      <div className={proseClasses}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default Text;
